import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFacilities } from '../hooks/useFacilities';
import { toast } from 'sonner';
import { ChevronLeft, ShoppingBag, Plus, Minus, Clock, Check } from 'lucide-react';

import { todayIST, currentISTHour } from '../../utils/ist';

const PERIOD_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

// Start hour from a slot_code ("0600-0730" → 6) — for past-checks and time-of-day grouping.
const slotStartHour = (slotId) => parseInt(String(slotId).slice(0, 2), 10);

function slotPeriod(slotId) {
  const h = slotStartHour(slotId);
  return h >= 17 ? 'evening' : h >= 12 ? 'afternoon' : 'morning';
}

// Inline style helpers — keeps JSX compact without Tailwind or extra CSS classes
const s = {
  card:    { background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 14, padding: 20 },
  label:   { color: 'var(--ink-3)', fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 4 },
  inp:     { width: '100%', background: 'var(--surface-3)', border: '1px solid var(--line)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--ink)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
  row2:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  section: { display: 'flex', flexDirection: 'column', gap: 14 },
  hd:      { color: 'var(--ink)', fontWeight: 600, fontSize: 14, margin: 0 },
  sub:     { color: 'var(--ink-4)', fontSize: 12, marginTop: 2 },
  badge: (variant) => {
    const map = {
      red:    { background: 'var(--bad-bg)',  color: 'var(--bad-fg)',  border: '1px solid var(--bad-fg)' },
      green:  { background: 'var(--ok-bg)',   color: 'var(--ok-fg)',   border: '1px solid var(--ok-fg)' },
      yellow: { background: 'oklch(from var(--brand) l c h / 0.12)', color: 'var(--brand)', border: '1px solid var(--brand-4)' },
      warn:   { background: 'var(--warn-bg)', color: 'var(--warn-fg)', border: '1px solid var(--warn-fg)' },
    };
    return { ...map[variant], borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 };
  },
};

export default function AdminEditBooking() {
  const { api } = useAuth();
  const navigate  = useNavigate();
  const { state } = useLocation();

  const booking     = state?.booking;
  const group       = state?.group || (booking ? [booking] : []);
  const isRental    = booking?.booking_type === 'rental' || booking?.ground_id === '__rental__';
  const today       = todayIST();
  const originalDate = booking?.date;

  // ── Redirect if no booking in state ───────────────────────────────────────
  useEffect(() => {
    if (!booking) navigate('/admin/bookings');
  }, [booking, navigate]);

  if (!booking) return null;

  // ─────────────────────────────────────────────────────────────────────────
  // ALL hooks
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { facilities } = useFacilities();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [customerForm, setCustomerForm] = useState({
    customer_name:  booking.customer_name  || '',
    customer_phone: booking.customer_phone || '',
    notes:          booking.notes          || '',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [date, setDate] = useState(booking.date);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [selectedSlotIds, setSelectedSlotIds] = useState(
    () => new Set(group.map(b => b.slot_id))
  );

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [slotRentals, setSlotRentals] = useState(() => {
    const m = {};
    for (const b of group) {
      const byItem = {};
      for (const r of (b.rentals || [])) byItem[String(r.item_id)] = r.quantity;
      m[b.slot_id] = byItem;
    }
    return m;
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [slotsByGround, setSlotsByGround] = useState({});
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [rentalUsage, setRentalUsage]     = useState({});
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [slotsLoading, setSlotsLoading]   = useState(false);

  const firstWithPayment = group.find(b => b.payment?.amount) || booking;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [payment, setPayment] = useState({
    method:         firstWithPayment.payment?.method || 'Cash',
    amount:         firstWithPayment.payment?.amount != null ? String(firstWithPayment.payment.amount) : '',
    transaction_id: firstWithPayment.payment?.transaction_id || '',
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [saving, setSaving] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const fetchAvailability = useCallback(() => {
    if (!booking.location_id) return;
    setSlotsLoading(true);
    api.get('/api/slots/location-availability', {
      params: { location_id: booking.location_id, date },
    })
      .then(res => {
        setSlotsByGround(res.data.slots || {});
        setRentalUsage(res.data.rental_usage || {});
      })
      .catch(() => toast.error('Failed to load availability'))
      .finally(() => setSlotsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, booking.location_id, date]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

  // ─────────────────────────────────────────────────────────────────────────
  // Derived values
  const selectedFacility = facilities.find(f => f.location_id === booking.location_id);
  const rentalCatalogue  = selectedFacility?.rental_items || [];
  const facilityName     = selectedFacility?.name || `Location ${booking.location_id}`;
  const groundName       = !isRental
    ? (selectedFacility?.grounds?.find(g => g.id === booking.ground_id)?.name || booking.ground_id)
    : null;

  const originalSlotIds = new Set(group.map(b => b.slot_id));

  // ─────────────────────────────────────────────────────────────────────────
  // Slot lists
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const groundSlots = useMemo(() => {
    if (isRental) return [];
    const raw = slotsByGround[booking.ground_id] || [];
    return raw.map(sl => ({
      ...sl,
      available:      sl.available || (originalSlotIds.has(sl.id) && date === originalDate),
      isOriginalSlot: originalSlotIds.has(sl.id) && date === originalDate,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsByGround, booking.ground_id, date, originalDate, isRental]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const rentalSlots = useMemo(() => {
    if (!isRental) return [];
    // Rentals aren't tied to a court → union of every ground's custom slots.
    const byCode = new Map();
    for (const slots of Object.values(slotsByGround)) {
      for (const sl of (slots || [])) if (!byCode.has(sl.id)) byCode.set(sl.id, sl);
    }
    return [...byCode.values()]
      .sort((a, b) => (a.start_time || a.id).localeCompare(b.start_time || b.id))
      .map(sl => ({ ...sl, available: true }));
  }, [slotsByGround, isRental]);

  const allSlots = isRental ? rentalSlots : groundSlots;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const slotsByPeriod = useMemo(() => {
    const periods = { morning: [], afternoon: [], evening: [] };
    allSlots.forEach(sl => {
      const p = slotPeriod(sl.id);
      if (periods[p]) periods[p].push(sl);
    });
    return periods;
  }, [allSlots]);

  // ─────────────────────────────────────────────────────────────────────────
  // Helpers
  const isSlotPast = (slotId) => {
    if (date > today) return false;
    if (date < today) return true;
    return slotStartHour(slotId) < currentISTHour();
  };

  const getSlotQty = (slotId, itemId) =>
    (slotRentals[slotId] || {})[String(itemId)] || 0;

  const changeSlotQty = (slotId, itemId, delta, maxQty) =>
    setSlotRentals(prev => {
      const slotData = prev[slotId] || {};
      const current  = slotData[String(itemId)] || 0;
      const next     = Math.min(maxQty ?? 99, Math.max(0, current + delta));
      return { ...prev, [slotId]: { ...slotData, [String(itemId)]: next } };
    });

  const toggleSlot = (slot) => {
    if (isSlotPast(slot.id)) return;
    const isOtherBooked = !isRental && !slot.available && !selectedSlotIds.has(slot.id);
    if (isOtherBooked) return;
    if (selectedSlotIds.has(slot.id)) {
      setSelectedSlotIds(prev => { const n = new Set(prev); n.delete(slot.id); return n; });
    } else {
      setSelectedSlotIds(prev => new Set([...prev, slot.id]));
      setSlotRentals(prev => prev[slot.id] !== undefined ? prev : { ...prev, [slot.id]: {} });
    }
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);
    if (newDate === originalDate) {
      setSelectedSlotIds(new Set(group.map(b => b.slot_id)));
      const restored = {};
      for (const b of group) {
        const byItem = {};
        for (const r of (b.rentals || [])) byItem[String(r.item_id)] = r.quantity;
        restored[b.slot_id] = byItem;
      }
      setSlotRentals(restored);
    } else {
      setSelectedSlotIds(new Set());
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Pricing
  const getSlotPrice = (slotId) => {
    if (isRental) return 0;
    // Keep the original price snapshot for unchanged slots (historical accuracy);
    // for new slots (or a changed date), use the ground's current slot price.
    const orig = group.find(b => b.slot_id === slotId);
    if (orig && date === originalDate && orig.court_price != null) return orig.court_price;
    const slot = (slotsByGround[booking.ground_id] || []).find(s => s.id === slotId);
    return slot?.price || 0;
  };

  const grandTotal = [...selectedSlotIds].reduce((total, slotId) => {
    const courtCost  = getSlotPrice(slotId);
    const rentalCost = rentalCatalogue.reduce((rs, item) =>
      rs + getSlotQty(slotId, item.item_id) * item.price_per_session, 0);
    return total + courtCost + rentalCost;
  }, 0);

  const collected  = parseFloat(payment.amount) || 0;
  const autoStatus = grandTotal > 0
    ? (collected >= grandTotal ? 'Paid' : 'Pending')
    : (collected > 0 ? 'Paid' : 'Pending');

  // ─────────────────────────────────────────────────────────────────────────
  // Equipment availability
  const alreadyUsedAtSlot = (slotId, itemId) => {
    const bucket = rentalUsage[slotId] || {};
    const id = String(itemId);
    return bucket[id] ?? bucket[itemId] ?? 0;
  };

  const effectiveStockAtSlot = (slotId, item) => {
    const origBooking = group.find(b => b.slot_id === slotId);
    const ourQty = origBooking
      ? ((origBooking.rentals || []).find(r => String(r.item_id) === String(item.item_id))?.quantity || 0)
      : 0;
    const used = alreadyUsedAtSlot(slotId, item.item_id);
    return Math.max(0, (item.quantity ?? 99) - used + ourQty);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Rentals payload builder
  const buildRentalsData = (slotQtyMap) =>
    rentalCatalogue
      .filter(item => (slotQtyMap[String(item.item_id)] || 0) > 0)
      .map(item => ({
        item_id:        item.item_id,
        name:           item.name,
        quantity:       slotQtyMap[String(item.item_id)],
        price_per_item: item.price_per_session,
        total:          slotQtyMap[String(item.item_id)] * item.price_per_session,
      }));

  // ─────────────────────────────────────────────────────────────────────────
  // Save
  const handleSave = async () => {
    if (!customerForm.customer_name.trim()) { toast.error('Name is required'); return; }
    if (selectedSlotIds.size === 0) { toast.error('Select at least one time slot'); return; }
    setSaving(true);

    try {
      const dateChanged = date !== originalDate;

      const cancelBookings = dateChanged
        ? group
        : group.filter(b => !selectedSlotIds.has(b.slot_id));

      const keepSlotIds = dateChanged
        ? []
        : [...selectedSlotIds].filter(id => originalSlotIds.has(id));

      const createSlotIds = dateChanged
        ? [...selectedSlotIds]
        : [...selectedSlotIds].filter(id => !originalSlotIds.has(id));

      if (cancelBookings.length > 0) {
        await Promise.all(cancelBookings.map(b =>
          api.patch(`/api/bookings/${b.booking_id}/cancel`)
        ));
      }

      await Promise.all(keepSlotIds.map(slotId => {
        const b = group.find(g => g.slot_id === slotId);
        const rentalsData = buildRentalsData(slotRentals[slotId] || {});
        return Promise.all([
          api.patch(`/api/bookings/${b.booking_id}/customer`, {
            customer_name:  customerForm.customer_name.trim(),
            customer_phone: customerForm.customer_phone.trim(),
            notes:          customerForm.notes.trim() || null,
          }),
          api.patch(`/api/bookings/${b.booking_id}/rentals`, { rentals: rentalsData }),
        ]);
      }));

      const sessionId = group[0]?.session_id || crypto.randomUUID();
      let firstCreatedBooking = null;
      if (createSlotIds.length > 0) {
        const results = await Promise.all(createSlotIds.map(slotId => {
          const slotInfo    = allSlots.find(sl => sl.id === slotId);
          const rentalsData = buildRentalsData(slotRentals[slotId] || {});
          return api.post('/api/bookings/', {
            booking_type:   isRental ? 'rental' : 'court',
            location_id:    booking.location_id,
            ground_id:      isRental ? '__rental__' : booking.ground_id,
            date,
            slot_id:        slotId,
            slot_time:      slotInfo?.time || slotId,
            customer_name:  customerForm.customer_name.trim(),
            customer_phone: customerForm.customer_phone.trim(),
            notes:          customerForm.notes.trim() || null,
            rentals:        rentalsData,
            session_id:     sessionId,
          });
        }));
        firstCreatedBooking = results[0]?.data;
      }

      if (payment.amount && collected > 0) {
        const payTarget = keepSlotIds.length > 0
          ? group.find(b => b.slot_id === keepSlotIds[0])
          : firstCreatedBooking;
        if (payTarget) {
          await api.patch(`/api/bookings/${payTarget.booking_id}/payment`, {
            method:         payment.method,
            amount:         collected,
            status:         autoStatus,
            transaction_id: payment.method === 'UPI' ? payment.transaction_id : null,
          });
        }
      }

      toast.success('Booking updated');
      navigate('/admin/bookings');
    } catch (err) {
      if (err?.response?.status === 409) toast.error('That slot is already booked — choose another');
      else toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Summary badge counts
  const cancelledCount = date !== originalDate
    ? group.length
    : group.filter(b => !selectedSlotIds.has(b.slot_id)).length;

  const newCount = date !== originalDate
    ? selectedSlotIds.size
    : [...selectedSlotIds].filter(id => !originalSlotIds.has(id)).length;

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  return (
    <div style={{ maxWidth: 680 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate('/admin/bookings')}
          className="sb-btn ghost icon"
        >
          <ChevronLeft size={18} />
        </button>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: 'var(--ink)', display: 'flex', alignItems: 'center', gap: 8 }}>
            {isRental && <ShoppingBag size={18} style={{ color: 'var(--brand)' }} />}
            Edit {isRental ? 'Rental ' : ''}Booking
          </h1>
          <p style={{ margin: '2px 0 0', color: 'var(--ink-4)', fontSize: 13 }}>
            {booking.customer_name} · {facilityName}{!isRental ? ` / ${groundName}` : ''}
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── Customer Details ─────────────────────────────────────────────── */}
        <div style={{ ...s.card, ...s.section }}>
          <h2 style={s.hd}>Customer Details</h2>
          <div style={s.row2}>
            <div>
              <label style={s.label}>Name</label>
              <input
                type="text"
                value={customerForm.customer_name}
                onChange={e => setCustomerForm(f => ({ ...f, customer_name: e.target.value }))}
                style={s.inp}
                placeholder="Customer name"
              />
            </div>
            <div>
              <label style={s.label}>Phone</label>
              <input
                type="tel"
                value={customerForm.customer_phone}
                onChange={e => setCustomerForm(f => ({ ...f, customer_phone: e.target.value }))}
                style={s.inp}
                placeholder="+91 98765 43210"
              />
            </div>
          </div>
          <div>
            <label style={s.label}>
              Notes <span style={{ color: 'var(--ink-4)', fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              rows={2}
              value={customerForm.notes}
              onChange={e => setCustomerForm(f => ({ ...f, notes: e.target.value }))}
              style={{ ...s.inp, resize: 'none' }}
              placeholder="Any special requirements…"
            />
          </div>
        </div>

        {/* ── Date & Slot Grid ─────────────────────────────────────────────── */}
        <div style={{ ...s.card, ...s.section }}>
          {/* Section header */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <h2 style={s.hd}>{isRental ? 'Hours' : 'Slots'}</h2>
              <p style={s.sub}>{facilityName}{!isRental ? ` · ${groundName}` : ''}</p>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {cancelledCount > 0 && (
                <span style={s.badge('red')}>{cancelledCount} will cancel</span>
              )}
              {newCount > 0 && (
                <span style={s.badge('green')}>+{newCount} new</span>
              )}
              {selectedSlotIds.size > 0 && (
                <span style={s.badge('yellow')}>{selectedSlotIds.size} selected</span>
              )}
            </div>
          </div>

          {/* Date picker */}
          <div>
            <label style={s.label}>Date</label>
            <input
              type="date"
              value={date}
              onChange={e => handleDateChange(e.target.value)}
              style={{ ...s.inp, width: 'auto' }}
            />
          </div>

          {/* Date-changed warning */}
          {date !== originalDate && (
            <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: 'var(--warn-bg)', border: '1px solid var(--warn-fg)', borderRadius: 10 }}>
              <span style={{ color: 'var(--warn-fg)', fontSize: 14 }}>⚠</span>
              <span style={{ color: 'var(--warn-fg)', fontSize: 12, lineHeight: 1.5 }}>
                Date changed from <strong>{originalDate}</strong> to <strong>{date}</strong>.
                All {group.length} original slot{group.length !== 1 ? 's' : ''} will be cancelled
                and your new selection will be created.
              </span>
            </div>
          )}

          {/* Slot grid */}
          {slotsLoading ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} style={{ height: 56, background: 'var(--surface-3)', borderRadius: 10, animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : allSlots.length === 0 ? (
            <p style={{ color: 'var(--ink-4)', fontSize: 13, textAlign: 'center', padding: '24px 0', fontStyle: 'italic' }}>
              No slots available for this date
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {(['morning', 'afternoon', 'evening']).map(period => {
                const slots = slotsByPeriod[period];
                if (!slots?.length) return null;
                return (
                  <div key={period}>
                    <p style={{ color: 'var(--ink-4)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                      {PERIOD_LABELS[period]}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                      {slots.map(slot => {
                        const isPast      = isSlotPast(slot.id);
                        const isSelected  = selectedSlotIds.has(slot.id);
                        const isOriginal  = originalSlotIds.has(slot.id) && date === originalDate;
                        const isOtherBook = !isRental && !slot.available && !isOriginal;
                        const info        = slot.booking_info;
                        const isNewSel    = isSelected && !isOriginal;

                        let cellStyle;
                        if (isPast && !isSelected) {
                          cellStyle = { background: 'var(--surface-3)', color: 'var(--ink-4)', cursor: 'not-allowed', textDecoration: 'line-through' };
                        } else if (isOtherBook && !isSelected) {
                          cellStyle = { background: 'var(--bad-bg)', color: 'var(--bad-fg)', cursor: 'not-allowed' };
                        } else if (isSelected && isNewSel) {
                          cellStyle = { background: 'var(--brand)', color: 'var(--brand-ink)', boxShadow: '0 0 0 2px var(--brand-4)' };
                        } else if (isSelected) {
                          cellStyle = { background: 'var(--brand)', color: 'var(--brand-ink)' };
                        } else if (isOriginal) {
                          cellStyle = { background: 'var(--surface-3)', color: 'var(--ink-2)', border: '1px solid var(--brand-4)' };
                        } else {
                          cellStyle = { background: 'var(--surface-3)', color: 'var(--ink-3)' };
                        }

                        return (
                          <div key={slot.id} style={{ position: 'relative' }}>
                            <button
                              type="button"
                              disabled={isPast || (isOtherBook && !isSelected)}
                              onClick={() => toggleSlot(slot)}
                              style={{
                                width: '100%', padding: '8px 4px', borderRadius: 10, border: 'none',
                                fontSize: 11, fontWeight: 600, textAlign: 'center', cursor: 'pointer',
                                fontFamily: 'inherit', transition: 'background .12s',
                                ...cellStyle,
                              }}
                            >
                              <div style={{ fontWeight: 700 }}>{slot.start_time || slot.time}</div>
                              <div style={{ marginTop: 2, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isPast ? '—'
                                : isOtherBook ? 'Booked'
                                : isSelected ? <Check size={11} />
                                : isOriginal ? 'Current'
                                : '○'}
                              </div>
                            </button>
                            {/* Tooltip for booked-by-others */}
                            {isOtherBook && info && (
                              <div className="sb-slot-tooltip">
                                <p style={{ fontWeight: 600, fontSize: 11 }}>{info.customer_name}</p>
                                {info.customer_phone && <p style={{ color: 'var(--ink-3)', fontSize: 10 }}>{info.customer_phone}</p>}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Legend */}
          {allSlots.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 11, color: 'var(--ink-4)' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--brand)', display: 'inline-block' }} /> Selected
              </span>
              {!isRental && date === originalDate && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--surface-3)', border: '1px solid var(--brand-4)', display: 'inline-block' }} /> Current
                </span>
              )}
              {!isRental && (
                <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--bad-bg)', display: 'inline-block' }} /> Booked
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: 'var(--surface-3)', opacity: 0.4, display: 'inline-block' }} /> Past
              </span>
            </div>
          )}
        </div>

        {/* ── Equipment per Slot ───────────────────────────────────────────── */}
        {rentalCatalogue.length > 0 && selectedSlotIds.size > 0 && (
          <div style={{ ...s.card, ...s.section }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ShoppingBag size={15} style={{ color: 'var(--brand)' }} />
              <h2 style={{ ...s.hd, margin: 0 }}>
                Equipment
                <span style={{ color: 'var(--ink-4)', fontWeight: 400, fontSize: 12, marginLeft: 6 }}>per slot</span>
              </h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[...selectedSlotIds].sort().map(slotId => {
                const slotInfo = allSlots.find(sl => sl.id === slotId);
                const slotTime = slotInfo?.time || slotId;
                const isNew    = !originalSlotIds.has(slotId) || date !== originalDate;
                const slotEquipTotal = rentalCatalogue.reduce((acc, item) =>
                  acc + getSlotQty(slotId, item.item_id) * item.price_per_session, 0);

                return (
                  <div key={slotId} style={{ background: 'var(--surface-3)', borderRadius: 12, overflow: 'hidden' }}>
                    {/* Slot header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--line)' }}>
                      <Clock size={13} style={{ color: 'var(--brand)', flexShrink: 0 }} />
                      <span style={{ color: 'var(--ink)', fontSize: 13, fontWeight: 600, flex: 1 }}>{slotTime}</span>
                      {isNew && (
                        <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 5, background: 'var(--ok-bg)', color: 'var(--ok-fg)', border: '1px solid var(--ok-fg)' }}>
                          New
                        </span>
                      )}
                      {slotEquipTotal > 0 && (
                        <span style={{ color: 'var(--brand)', fontSize: 12, fontWeight: 600 }}>₹{slotEquipTotal}</span>
                      )}
                    </div>

                    {/* Items */}
                    <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {rentalCatalogue.map(item => {
                        const avail = effectiveStockAtSlot(slotId, item);
                        const qty   = Math.min(getSlotQty(slotId, item.item_id), avail);
                        const pct   = item.quantity > 0 ? Math.max(0, avail) / item.quantity : 1;
                        const lineTotal = qty * item.price_per_session;
                        const availColor = avail === 0 ? 'var(--bad-fg)' : avail <= 3 ? 'var(--warn-fg)' : 'var(--ink-4)';
                        const barColor   = avail === 0 ? 'var(--bad-fg)' : avail <= 3 ? 'var(--warn-fg)' : 'var(--ok-fg)';

                        return (
                          <div key={item.item_id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            {/* Name + bar */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                <span style={{ color: 'var(--ink-2)', fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
                                  {item.name}
                                </span>
                                <span style={{ color: availColor, fontSize: 11, flexShrink: 0 }}>
                                  {avail === 0 ? 'Full' : `${avail} avail`}
                                </span>
                              </div>
                              <div style={{ height: 4, borderRadius: 4, background: 'var(--line)' }}>
                                <div style={{ height: 4, borderRadius: 4, background: barColor, width: `${Math.round(pct * 100)}%`, transition: 'width .2s' }} />
                              </div>
                            </div>
                            {/* Stepper */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              <button type="button"
                                onClick={() => changeSlotQty(slotId, item.item_id, -1, avail)}
                                disabled={qty === 0}
                                style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ink-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: qty === 0 ? 0.3 : 1 }}>
                                <Minus size={11} />
                              </button>
                              <span style={{ color: 'var(--ink)', fontWeight: 700, width: 20, textAlign: 'center', fontSize: 13 }}>{qty}</span>
                              <button type="button"
                                onClick={() => changeSlotQty(slotId, item.item_id, 1, avail)}
                                disabled={qty >= avail || avail === 0}
                                style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--ink-3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: (qty >= avail || avail === 0) ? 0.3 : 1 }}>
                                <Plus size={11} />
                              </button>
                            </div>
                            {/* Line total */}
                            <div style={{ fontSize: 12, flexShrink: 0, width: 60, textAlign: 'right' }}>
                              {lineTotal > 0
                                ? <span style={{ color: 'var(--brand)', fontWeight: 600 }}>₹{lineTotal}</span>
                                : <span style={{ color: 'var(--ink-4)' }}>₹{item.price_per_session}/hr</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Grand Total Breakdown ────────────────────────────────────────── */}
        {grandTotal > 0 && (
          <div style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 14, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[...selectedSlotIds].sort().map(slotId => {
              const slotInfo   = allSlots.find(sl => sl.id === slotId);
              const slotTime   = slotInfo?.time || slotId;
              const courtCost  = getSlotPrice(slotId);
              const rentalCost = rentalCatalogue.reduce((acc, item) =>
                acc + getSlotQty(slotId, item.item_id) * item.price_per_session, 0);
              const slotTotal  = courtCost + rentalCost;
              if (slotTotal === 0) return null;
              return (
                <div key={slotId} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: 'var(--ink-3)' }}>{slotTime}</span>
                  <span style={{ color: 'var(--ink-2)' }}>₹{slotTotal}</span>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid var(--line)' }}>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>Total</span>
              <span style={{ color: 'var(--brand)', fontWeight: 700, fontSize: 17 }}>₹{grandTotal}</span>
            </div>
          </div>
        )}

        {/* ── Payment ─────────────────────────────────────────────────────── */}
        <div style={{ ...s.card, ...s.section }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={s.hd}>Payment</h2>
            {grandTotal > 0 && (
              <div style={{ textAlign: 'right' }}>
                <p style={{ color: 'var(--ink-4)', fontSize: 11, margin: 0 }}>Total due</p>
                <p style={{ color: 'var(--brand)', fontWeight: 700, fontSize: 16, margin: 0 }}>₹{grandTotal}</p>
              </div>
            )}
          </div>

          {grandTotal > 0 && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button"
                onClick={() => setPayment(p => ({ ...p, amount: String(grandTotal) }))}
                style={{ flex: 1, padding: '8px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, background: 'var(--ok-bg)', border: '1px solid var(--ok-fg)', color: 'var(--ok-fg)', cursor: 'pointer' }}>
                ✓ Full amount (₹{grandTotal})
              </button>
              {collected > 0 && (
                <button type="button"
                  onClick={() => setPayment(p => ({ ...p, amount: '' }))}
                  style={{ padding: '8px 14px', fontSize: 12, borderRadius: 8, background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--ink-3)', cursor: 'pointer' }}>
                  Clear
                </button>
              )}
            </div>
          )}

          <div style={s.row2}>
            <div>
              <label style={s.label}>Method</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['Cash', 'UPI'].map(m => (
                  <button key={m} type="button"
                    onClick={() => setPayment(p => ({ ...p, method: m }))}
                    style={{
                      flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      background: payment.method === m ? 'var(--brand)' : 'var(--surface-3)',
                      color:      payment.method === m ? 'var(--brand-ink)' : 'var(--ink-3)',
                      border:     payment.method === m ? '1px solid var(--brand)' : '1px solid var(--line)',
                    }}>{m}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={s.label}>Amount Collected (₹)</label>
              <input type="number" value={payment.amount}
                onChange={e => setPayment(p => ({ ...p, amount: e.target.value }))}
                style={s.inp}
                placeholder="0" />
            </div>
          </div>

          {payment.amount && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', background: 'var(--surface-3)', borderRadius: 8, fontSize: 13 }}>
                <span style={{ color: 'var(--ink-3)' }}>Status</span>
                <span className={`sb-badge ${autoStatus === 'Paid' ? 'ok' : 'warn'}`}>
                  {autoStatus === 'Paid'
                    ? '✓ Paid'
                    : `Pending${grandTotal > 0 ? ` · ₹${Math.max(0, grandTotal - collected)} remaining` : ''}`}
                </span>
              </div>
              {payment.method === 'UPI' && (
                <div>
                  <label style={s.label}>UPI Transaction ID</label>
                  <input type="text" value={payment.transaction_id}
                    onChange={e => setPayment(p => ({ ...p, transaction_id: e.target.value }))}
                    style={s.inp}
                    placeholder="e.g. 3289xxxx" />
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Save ────────────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 12, paddingBottom: 32 }}>
          <button type="button" onClick={() => navigate('/admin/bookings')}
            style={{ flex: 1, padding: '13px 0', fontSize: 14, fontWeight: 500, borderRadius: 12, background: 'var(--surface-3)', border: '1px solid var(--line)', color: 'var(--ink-3)', cursor: 'pointer' }}>
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '13px 0', fontSize: 14, fontWeight: 700, borderRadius: 12, background: 'var(--brand)', color: 'var(--brand-ink)', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>

      </div>
    </div>
  );
}
